<template>
    <app-layout>
        <div class="flex flex-col">
            <page-header title="Compliance: Testing & Calibration">
                <div class="mt-3 flex items-start justify-between">
                    <filter-bar v-model="queryParams" :config="filterBarConfig" />
                </div>
            </page-header>
        </div>

        <div class="position-relative table-responsive">
            <table class="table-rounded table-hover table border">
                <thead>
                    <tr>
                        <th class="bg-white align-middle" rowspan="2" />
                        <th class="bg-white" rowspan="2" />
                        <th class="text-center align-middle" rowspan="2">Required</th>
                        <th class="text-center align-middle" rowspan="2">Performed</th>
                        <th class="!border-e-3 border-black text-center align-middle" rowspan="2">Approved</th>
                        <th class="text-center align-middle" colspan="2">Morning</th>
                        <th class="text-center align-middle" colspan="2">Evening</th>
                        <th class="text-center align-middle" colspan="2">Overnight</th>
                    </tr>
                    <tr>
                        <th class="text-center align-middle">Performed</th>
                        <th class="text-center align-middle">Approved</th>
                        <th class="text-center align-middle">Performed</th>
                        <th class="text-center align-middle">Approved</th>
                        <th class="text-center align-middle">Performed</th>
                        <th class="text-center align-middle">Approved</th>
                    </tr>
                    <tr>
                        <th class="!border-y-3 !border-s-3 border-black bg-white">TOTAL</th>
                        <th class="!border-y-3 border-black bg-white" />
                        <th class="!border-y-3 border-black text-center">{{ total(data, 'required') }}</th>
                        <th
                            :class="total(data, 'required') === total(data, 'performed') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'"
                            class="!border-y-3 border-black text-center"
                        >
                            {{ total(data, 'performed') }}/{{ total(data, 'required') }}
                        </th>
                        <th class="!border-y-3 !border-e-3 border-black text-center">
                            {{ total(data, 'approved') }}
                        </th>
                        <th
                            :class="requirementsMet(data, 'morning') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'"
                            class="!border-y-3 border-black text-center"
                        >
                            {{ getShiftPerformedValue(data, 'morning') }}
                        </th>
                        <th class="!border-y-3 border-black text-center">
                            {{ getShiftApprovedValue(data, 'morning') }}
                        </th>
                        <th
                            :class="requirementsMet(data, 'evening') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'"
                            class="!border-y-3 border-black text-center"
                        >
                            {{ getShiftPerformedValue(data, 'evening') }}
                        </th>
                        <th class="!border-y-3 border-black text-center">
                            {{ getShiftApprovedValue(data, 'evening') }}
                        </th>
                        <th
                            :class="requirementsMet(data, 'overnight') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'"
                            class="!border-y-3 border-black text-center"
                        >
                            {{ getShiftPerformedValue(data, 'overnight') }}
                        </th>
                        <th class="!border-y-3 !border-e-3 border-black text-center">
                            {{ getShiftApprovedValue(data, 'overnight') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="group in groupedData" :key="group.area_id">
                        <tr class="cursor-pointer bg-gray-50" @click.stop="toggleGroup(group.area_id)">
                            <td class="truncate !bg-gray-200 font-semibold">{{ group.area_name }}</td>
                            <td class="!bg-gray-200 font-semibold">{{ expandedGroups.has(group.area_id) ? 'Curie ID' : '' }}</td>
                            <td class="!bg-[#dadee3] text-center">{{ total(group.items, 'required') }}</td>
                            <td
                                :class="
                                    total(group.items, 'required') === total(group.items, 'performed') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'
                                "
                                class="text-center"
                            >
                                {{ total(group.items, 'performed') }}/{{ total(group.items, 'required') }}
                            </td>
                            <td class="!border-e-3 border-black !bg-gray-200 text-center">{{ total(group.items, 'approved') }}</td>
                            <td :class="requirementsMet(group.items, 'morning') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                {{ getShiftPerformedValue(group.items, 'morning') }}
                            </td>
                            <td class="!bg-gray-200 text-center">{{ getShiftApprovedValue(group.items, 'morning') }}</td>
                            <td :class="requirementsMet(group.items, 'evening') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                {{ getShiftPerformedValue(group.items, 'evening') }}
                            </td>
                            <td class="!bg-gray-200 text-center">{{ getShiftApprovedValue(group.items, 'evening') }}</td>
                            <td :class="requirementsMet(group.items, 'overnight') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                {{ getShiftPerformedValue(group.items, 'overnight') }}
                            </td>
                            <td class="!bg-gray-200 text-center">{{ getShiftApprovedValue(group.items, 'overnight') }}</td>
                        </tr>
                        <template v-if="expandedGroups.has(group.area_id)">
                            <tr v-for="item in group.items" :key="item.curie_id" class="cursor-pointer" @click.stop="openAssetDetails(item.asset_id)">
                                <td>{{ item.name }}</td>
                                <td :data-task-ids="item.ids">{{ item.curie_id }}</td>
                                <td class="!bg-[#dadee3] text-center">{{ item.required }}</td>
                                <td :class="item.performed === item.required ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                    {{ item.performed }}/{{ item.required }}
                                </td>
                                <td class="!border-e-3 border-black !bg-gray-200 text-center">{{ item.approved }}</td>
                                <td :class="requirementsMet(item, 'morning') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                    {{ getShiftPerformedValue(item, 'morning') }}
                                </td>
                                <td class="!bg-gray-200 text-center">{{ getShiftApprovedValue(item, 'morning') }}</td>
                                <td :class="requirementsMet(item, 'evening') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                    {{ getShiftPerformedValue(item, 'evening') }}
                                </td>
                                <td class="!bg-gray-200 text-center">{{ getShiftApprovedValue(item, 'evening') }}</td>
                                <td :class="requirementsMet(item, 'overnight') ? '!bg-[#10b981] text-white' : '!bg-[#ebb6b7]'" class="text-center">
                                    {{ getShiftPerformedValue(item, 'overnight') }}
                                </td>
                                <td class="!bg-gray-200 text-center">{{ getShiftApprovedValue(item, 'overnight') }}</td>
                            </tr>
                        </template>
                    </template>
                </tbody>
            </table>
        </div>
        <div class="text-small mt-2 text-muted">
            This report includes all tasks due between {{ dateTimeMedium(startDate, currentLocale) }} and
            {{ dateTimeMedium(endDate, currentLocale) }}
        </div>
    </app-layout>
</template>

<script setup>
import { computed, ref } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Components/PageHeader.vue';
import { trans } from '@/mixins/i18n.js';
import FilterBar from '@/Components/FilterBar.vue';
import { route } from 'ziggy-js';
import { router, usePage } from '@inertiajs/vue3';
import { DateTime } from 'luxon';
import dateTimeMedium from '@/filters/dateTimeMedium.js';

const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
});

const expandedGroups = ref(new Set());
const queryParams = ref(null);

const filterBarConfig = computed(() => ({
    route: route('reports.testing-compliance'),
    routeOptions: { only: ['data'] },
    filters: [{ type: 'date-range', label: trans('activities.overview.cell.date'), startKey: 'start', endKey: 'end' }],
}));

const currentLocale = usePage().props.locale;

const startDate = computed(() => {
    const param = queryParams.value?.form.start;
    const dateStr = param || DateTime.now().toISODate();
    return DateTime.fromISO(`${dateStr}T05:00:00`);
});

const endDate = computed(() => {
    const param = queryParams.value?.form.end;
    const dateStr = param || DateTime.now().toISODate();
    return DateTime.fromISO(`${dateStr}T05:00:00`).plus({ days: 1 });
});

const groupedData = computed(() => {
    const map = props.data.reduce((acc, item) => {
        if (!acc[item.area_id]) {
            acc[item.area_id] = {
                area_id: item.area_id,
                area_name: item.area_name,
                items: [],
            };
        }
        acc[item.area_id].items.push(item);
        return acc;
    }, {});
    return Object.values(map).sort((a, b) => a.area_name.localeCompare(b.area_name));
});

function requirementsMet(data, shift) {
    const required = total(data, `required_${shift}`);
    const performed = total(data, `performed_${shift}`);
    return performed === required;
}

function notPerformed(data) {
    const required = total(data, 'required');
    const performed = total(data, 'performed');
    return required - performed;
}

function total(items, attr) {
    if (Array.isArray(items)) {
        return items.reduce((acc, item) => acc + (item?.[attr] ?? 0), 0);
    }
    return items?.[attr] ?? 0;
}

function getShiftPerformedValue(items, shift) {
    if (!items) return;

    const required = Array.isArray(items) ? total(items, `required_${shift}`) : items[`required_${shift}`];
    const performed = Array.isArray(items) ? total(items, `performed_${shift}`) : items[`performed_${shift}`];

    if (required === performed) return required;
    return `${performed}/${required}`;
}

function getShiftApprovedValue(items, shift) {
    if (!items) return;

    return Array.isArray(items) ? total(items, `approved_${shift}`) : items[`approved_${shift}`];
}

function toggleGroup(areaId) {
    if (expandedGroups.value.has(areaId)) {
        expandedGroups.value.delete(areaId);
    } else {
        expandedGroups.value.add(areaId);
    }
}

function openAssetDetails(assetId) {
    router.visit(route('assets.show', assetId));
}
</script>
